import React from "react";
import { Link, useLocation } from "@tanstack/react-router";
import {
  BookOpenCheck,
  BriefcaseBusiness,
  CalendarDays,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Handshake,
  LogOut,
  MessageSquareQuote,
  Moon,
  Pin,
  PinOff,
  ReceiptText,
  ScanLine,
  Settings,
  ShieldCheck,
  Sun,
  UserRound,
} from "lucide-react";
import { useAuth } from "@core/auth/AuthProvider";
import { Flyover } from "@core/components/Flyover";
import { VenProLogo } from "@core/components/VenProLogo";
import { getModuleNavigation, getModuleSidebarAction, getModuleTopbarItems, orderModuleItems } from "@core/module-registry";
import { useOrganization } from "@core/organizations/OrganizationProvider";
import { useAppPreferences } from "@core/preferences/AppPreferencesProvider";

const iconMap = { BriefcaseBusiness, BookOpenCheck, CalendarDays, Handshake, MessageSquareQuote, ReceiptText, ScanLine, ShieldCheck };
const navigation = getModuleNavigation();
const topbarItems = getModuleTopbarItems();

function getVendorShortName(organization) {
  const explicit = String(organization.shortName ?? "").trim();
  if (explicit) return explicit.slice(0, 10).toUpperCase();
  return String(organization.name ?? "Vendor").trim().split(/\s+/)[0].slice(0, 10).toUpperCase();
}

export function SidebarNav({ expanded, onHoverChange }) {
  const { user, signOut } = useAuth();
  const location = useLocation();
  const { organizations, activeOrganization, selectOrganization, can, isSuperadmin } = useOrganization();
  const { preferences, setPreference, isModuleVisible } = useAppPreferences();
  const [accountOpen, setAccountOpen] = React.useState(false);
  const [sidebarActionOpen, setSidebarActionOpen] = React.useState(false);
  const sidebarRef = React.useRef(null);
  const collapsed = !expanded;
  const collapseMode = preferences.sidebarCollapseMode ?? "dynamic";
  const textOnly = expanded && preferences.sidebarDisplayMode === "text-only";
  const visibleNavigation = orderModuleItems(
    navigation.filter((item) => can(item.permission) && isModuleVisible(item.moduleId)),
    preferences.navigationOrder?.sidebar,
  );
  const footerActionItem = visibleNavigation.find((item) => item.placement === "footer");
  const primaryNavigation = visibleNavigation.filter((item) => item.placement !== "footer");
  const SidebarAction = footerActionItem ? getModuleSidebarAction(footerActionItem.moduleId) : null;
  const visibleTools = orderModuleItems(
    topbarItems.filter((item) => can(item.permission) && isModuleVisible(item.moduleId)),
    preferences.navigationOrder?.topbar,
  );
  const displayName = preferences.preferredName || user.displayName || "Vendor user";
  const roleLabel = isSuperadmin ? "VenPro superadmin" : activeOrganization.role.replaceAll("_", " ");
  const vendorShortName = getVendorShortName(activeOrganization);
  const ThemeIcon = preferences.theme === "light" ? Sun : Moon;
  const footerFlyoutOpen = accountOpen || sidebarActionOpen;

  const keepSidebarExpanded = React.useCallback(() => {
    onHoverChange(true);
  }, [onHoverChange]);

  const releaseSidebarIfIdle = React.useCallback(() => {
    window.requestAnimationFrame(() => {
      const sidebar = sidebarRef.current;
      if (!sidebar || footerFlyoutOpen) return;
      const focusInside = sidebar.contains(document.activeElement);
      if (!sidebar.matches(":hover") && !focusInside) onHoverChange(false);
    });
  }, [footerFlyoutOpen, onHoverChange]);

  React.useEffect(() => {
    setAccountOpen(false);
    setSidebarActionOpen(false);
  }, [location.pathname]);

  React.useEffect(() => {
    if (footerFlyoutOpen) keepSidebarExpanded();
    else releaseSidebarIfIdle();
  }, [footerFlyoutOpen, keepSidebarExpanded, releaseSidebarIfIdle]);

  const cycleTheme = () => {
    const next = preferences.theme === "dark" ? "light" : preferences.theme === "light" ? "system" : "dark";
    setPreference("theme", next);
  };

  const toggleCollapsed = React.useCallback(() => {
    if (collapseMode === "collapsed") {
      setPreference("sidebarCollapseMode", "dynamic");
      setPreference("sidebarPinned", true);
      setPreference("sidebarCollapsed", false);
      return;
    }
    if (collapseMode === "expanded") {
      setPreference("sidebarCollapsed", !preferences.sidebarCollapsed);
      return;
    }
    if (!preferences.sidebarPinned && !preferences.sidebarCollapsed) {
      setPreference("sidebarPinned", true);
      setPreference("sidebarCollapsed", false);
      return;
    }
    setPreference("sidebarCollapsed", !preferences.sidebarCollapsed);
  }, [collapseMode, preferences.sidebarCollapsed, preferences.sidebarPinned, setPreference]);

  const togglePinned = () => {
    const pinned = !preferences.sidebarPinned;
    setPreference("sidebarPinned", pinned);
    setPreference("sidebarCollapsed", false);
  };

  React.useEffect(() => {
    const handleShortcut = (event) => {
      if (event.key.toLowerCase() !== "b" || (!event.ctrlKey && !event.metaKey)) return;
      event.preventDefault();
      toggleCollapsed();
    };
    window.addEventListener("keydown", handleShortcut);
    return () => window.removeEventListener("keydown", handleShortcut);
  }, [toggleCollapsed]);

  return (
    <aside
      ref={sidebarRef}
      className={`vp-sidebar ${collapsed ? "is-collapsed" : ""} ${textOnly ? "is-text-only" : ""}`}
      aria-label="Primary navigation"
      onMouseEnter={keepSidebarExpanded}
      onMouseLeave={releaseSidebarIfIdle}
      onFocusCapture={keepSidebarExpanded}
      onBlurCapture={releaseSidebarIfIdle}
    >
      {!preferences.topbarVisible && (
        <div className="vp-sidebar-brand">
          <Link
            to="/jobs"
            className="vp-sidebar-brand-link"
            aria-label={`${activeOrganization.name} jobs`}
            title={collapsed ? activeOrganization.name : "Jobs"}
          >
            <VenProLogo className="vp-sidebar-logo" />
            {collapsed && <span className="vp-sidebar-short-name">{vendorShortName}</span>}
          </Link>
          {!collapsed && (
            organizations.length > 1 ? (
              <label className="vp-sidebar-organization">
                <span className="sr-only">Active vendor</span>
                <select value={activeOrganization.id} onChange={(event) => selectOrganization(event.target.value)}>
                  {organizations.map((organization) => (
                    <option key={organization.id} value={organization.id}>{organization.name}</option>
                  ))}
                </select>
                <ChevronDown aria-hidden="true" />
              </label>
            ) : (
              <strong className="vp-sidebar-organization-name" title={activeOrganization.name}>{activeOrganization.name}</strong>
            )
          )}
        </div>
      )}
      <nav className={`vp-sidebar-nav vp-nav-${preferences.navItemSize}`}>
        {primaryNavigation.map((item) => {
          const Icon = iconMap[item.icon] ?? BriefcaseBusiness;
          const active = location.pathname === item.href || location.pathname.startsWith(`${item.href}/`);
          return (
            <Link
              key={item.id}
              to={item.href}
              className={`vp-sidebar-item ${active ? "is-active" : ""}`}
              title={collapsed ? item.label : undefined}
            >
              {!textOnly && <Icon aria-hidden="true" />}
              {!collapsed && <span>{item.label}</span>}
              {!collapsed && active && <ChevronRight className="vp-nav-chevron" aria-hidden="true" />}
            </Link>
          );
        })}
        {!preferences.topbarVisible && visibleTools.length > 0 && (
          <div className="vp-sidebar-tools" aria-label="Tools">
            {visibleTools.map((item) => {
              const Icon = iconMap[item.icon] ?? ScanLine;
              return (
                <Link
                  key={item.id}
                  to={item.href}
                  search={item.search}
                  className="vp-sidebar-item vp-sidebar-tool"
                  title={collapsed ? item.label : undefined}
                >
                  {!textOnly && <Icon aria-hidden="true" />}
                  {!collapsed && <span>{item.label}</span>}
                </Link>
              );
            })}
          </div>
        )}
      </nav>

      <div className="vp-sidebar-footer">
        <Flyover
          open={accountOpen}
          onOpenChange={(open) => {
            setSidebarActionOpen(false);
            setAccountOpen(open);
          }}
          role="menu"
          ariaLabel="Account menu"
          alignOffset={collapsed ? "calc(38px + 0.2rem)" : 0}
          anchorClassName={`vp-account-menu vp-sidebar-account vp-sidebar-account-controls ${collapsed ? "is-collapsed" : ""}`}
          panelClassName="vp-account-popover"
          width={224}
          trigger={({ triggerProps, toggle }) => (
            <>
              {collapsed && footerActionItem && SidebarAction && (
                <SidebarAction
                  item={footerActionItem}
                  active={location.pathname.startsWith(footerActionItem.href)}
                  open={sidebarActionOpen}
                  onOpenChange={(open) => {
                    setAccountOpen(false);
                    setSidebarActionOpen(open);
                  }}
                />
              )}
              <button
                {...triggerProps}
                className="vp-account-trigger"
                type="button"
                onClick={toggle}
                title={collapsed ? displayName : undefined}
              >
                <span className="vp-avatar" aria-hidden="true">{displayName.slice(0, 1)}</span>
                {!collapsed && (
                  <>
                    <span className="vp-account-copy">
                      <strong>{displayName}</strong>
                      <small>{roleLabel}</small>
                    </span>
                    <ChevronDown aria-hidden="true" />
                  </>
                )}
              </button>
              <button
                type="button"
                className="vp-sidebar-control vp-footer-icon-control"
                onClick={() => {
                  setAccountOpen(false);
                  setSidebarActionOpen(false);
                  cycleTheme();
                }}
                aria-label={`Theme mode: ${preferences.theme}. Change theme`}
                title={`Theme mode: ${preferences.theme}`}
              >
                <ThemeIcon aria-hidden="true" />
              </button>
              {!collapsed && footerActionItem && SidebarAction && (
                <SidebarAction
                  item={footerActionItem}
                  active={location.pathname.startsWith(footerActionItem.href)}
                  open={sidebarActionOpen}
                  onOpenChange={(open) => {
                    setAccountOpen(false);
                    setSidebarActionOpen(open);
                  }}
                />
              )}
            </>
          )}
        >
          {({ close }) => (
            <>
              <div className="vp-account-summary">
                <strong>{displayName}</strong>
                <span>{user.email || activeOrganization.name}</span>
              </div>
              <Link to="/settings/profile" role="menuitem" onClick={() => close("navigate")}>
                <UserRound /> Profile
              </Link>
              <Link to="/settings" role="menuitem" onClick={() => close("navigate")}>
                <Settings /> Settings
              </Link>
              <button
                type="button"
                role="menuitem"
                onClick={() => {
                  close("action");
                  void signOut();
                }}
              >
                <LogOut /> Sign out
              </button>
            </>
          )}
        </Flyover>
        {collapsed ? (
          <>
            {collapseMode === "collapsed" && (
              <Link
                to="/settings"
                className={`vp-sidebar-item ${location.pathname.startsWith("/settings") ? "is-active" : ""}`}
                aria-label="Settings"
                title="Settings"
              >
                <Settings aria-hidden="true" />
              </Link>
            )}
            <button
              type="button"
              className="vp-sidebar-control vp-sidebar-expand-only"
              onClick={toggleCollapsed}
              aria-label="Expand sidebar"
              title="Expand sidebar"
            >
              <ChevronRight />
            </button>
          </>
        ) : (
          <div className="vp-sidebar-footer-row vp-sidebar-layout-controls">
            <button
              type="button"
              className="vp-sidebar-control vp-collapse-control"
              onClick={toggleCollapsed}
              aria-label="Collapse sidebar"
              title="Collapse sidebar"
            >
              <ChevronLeft />
              <span>Collapse</span>
            </button>
            <button
              type="button"
              className="vp-sidebar-control vp-pin-control"
              onClick={togglePinned}
              aria-pressed={preferences.sidebarPinned}
              aria-label={preferences.sidebarPinned ? "Unpin sidebar" : "Pin sidebar"}
              title={preferences.sidebarPinned ? "Unpin sidebar" : "Pin sidebar"}
            >
              {preferences.sidebarPinned ? <Pin /> : <PinOff />}
            </button>
            <Link
              to="/settings"
              className={`vp-sidebar-control vp-settings-control ${location.pathname.startsWith("/settings") ? "is-active" : ""}`}
              aria-label="Settings"
              title="Settings"
            >
              <Settings aria-hidden="true" />
            </Link>
          </div>
        )}
      </div>
    </aside>
  );
}
